import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { FormEventHandler } from 'react';
import { useCostTypeOptions } from './useCostTypes';
import { calculateTotalCosts, CostLine, EntityType, EntityWithCosts, getEntityBudget } from './utils';

export type CostFormData = {
    description: string;
    amount: string;
    type: string;
    date_incurred: Date;
};

interface EditCostFormProps {
    entityType: EntityType;
    entity: EntityWithCosts;
    cost: CostLine;
    data: CostFormData;
    setData: (field: keyof CostFormData, value: string | number | Date | null | undefined) => void;
    errors: Partial<Record<keyof CostFormData, string>>;
    clearValidationError: (field: keyof CostFormData) => void;
    onSubmit: FormEventHandler<HTMLFormElement>;
    processing: boolean;
    onCancel?: () => void;
    dateOpen?: boolean;
    setDateOpen?: (open: boolean) => void;
    showBudget?: boolean;
}

export default function EditCostForm({
    entityType,
    entity,
    cost,
    data,
    setData,
    errors,
    clearValidationError,
    onSubmit,
    processing,
    onCancel,
    dateOpen,
    setDateOpen,
    showBudget = true,
}: EditCostFormProps) {
    const costTypeOptions = useCostTypeOptions();

    // Calculate budget information
    const totalCosts = calculateTotalCosts(entity.cost_lines);
    const budget = getEntityBudget(entity);
    const oldCostAmount = Number(cost.amount || 0);
    const newCostAmount = Number(data.amount || 0);
    // Calculate what costs would be after this edit (remove old cost, add new cost)
    const costsAfterEdit = totalCosts - oldCostAmount + newCostAmount;
    const remainingBudget = budget - totalCosts;
    const remainingAfterEdit = budget - costsAfterEdit;

    const descriptionError = errors.description;
    const typeError = errors.type;
    const amountError = errors.amount;
    const dateIncurredError = errors.date_incurred;

    return (
        <div>
            {showBudget && budget >= 0 && (
                <div className="bg-muted/50 mb-6 space-y-2 rounded-lg p-4">
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total {entityType === 'lead' ? 'Estimated Value' : 'Budget'}:</span>
                        <span className="font-medium">{formatCurrency(budget)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Current Costs:</span>
                        <span className="font-medium">{formatCurrency(totalCosts)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 text-sm">
                        <span className="text-muted-foreground">Remaining {entityType === 'lead' ? 'Value' : 'Budget'}:</span>
                        <span className={cn('font-medium', remainingBudget < 0 ? 'text-destructive' : 'text-success-subtle-foreground')}>
                            {formatCurrency(remainingBudget)}
                        </span>
                    </div>
                    {data.amount && (
                        <div className="flex justify-between border-t pt-2 text-sm">
                            <span className="text-muted-foreground">After This Update:</span>
                            <span className={cn('font-medium', remainingAfterEdit < 0 ? 'text-destructive' : 'text-success-subtle-foreground')}>
                                {formatCurrency(remainingAfterEdit)}
                            </span>
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={onSubmit} className="space-y-6">
                <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                        id="description"
                        value={data.description}
                        onChange={(e) => {
                            setData('description', e.target.value);
                            clearValidationError('description');
                        }}
                        placeholder="Describe what this cost is for..."
                        className={descriptionError ? 'border-destructive' : ''}
                    />
                    {descriptionError && <p className="text-destructive mt-1 text-sm">{descriptionError}</p>}
                </div>

                <div className="w-full space-y-2">
                    <Label htmlFor="type">Type</Label>
                    <Combobox
                        options={costTypeOptions}
                        value={data.type}
                        onValueChange={(value) => {
                            setData('type', value);
                            clearValidationError('type');
                        }}
                        placeholder="Select cost type"
                        className={typeError ? 'border-destructive' : ''}
                    />
                    {typeError && <p className="text-destructive mt-1 text-sm">{typeError}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="amount">Amount (EUR)</Label>
                    <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        value={data.amount}
                        onChange={(e) => {
                            setData('amount', e.target.value);
                            clearValidationError('amount');
                        }}
                        placeholder="0.00"
                        className={amountError ? 'border-destructive' : ''}
                    />
                    {amountError && <p className="text-destructive mt-1 text-sm">{amountError}</p>}
                </div>

                <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant={'outline'}
                                className={cn(
                                    'w-full justify-start text-left font-normal',
                                    !data.date_incurred && 'text-muted-foreground',
                                    dateIncurredError && 'border-destructive',
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {data.date_incurred ? formatDate(data.date_incurred) : 'Pick a date'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                            <Calendar
                                mode="single"
                                selected={data.date_incurred}
                                onSelect={(date) => {
                                    setData('date_incurred', date || new Date());
                                    clearValidationError('date_incurred');
                                    if (setDateOpen) setDateOpen(false);
                                }}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    {dateIncurredError && <p className="text-destructive mt-1 text-sm">{dateIncurredError}</p>}
                </div>

                {remainingAfterEdit < 0 && data.amount && showBudget && (
                    <div className="bg-danger-subtle rounded-lg border border-transparent p-4">
                        <div className="flex items-center">
                            <div className="text-danger-subtle-foreground text-sm">
                                ⚠️ Warning: This update will exceed the remaining {entityType === 'lead' ? 'value' : 'budget'} by{' '}
                                {formatCurrency(Math.abs(remainingAfterEdit))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-4 pt-4">
                    {onCancel && (
                        <Button type="button" variant="outline" onClick={onCancel}>
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Saving...' : 'Update Cost'}
                    </Button>
                </div>
            </form>
        </div>
    );
}
